import { zodResolver } from "@hookform/resolvers/zod";
import { add, format, formatISO } from "date-fns";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { Monitor } from "@oneleet/api_client";

import { AnimatePresence } from "framer-motion";

import {
  Button,
  FormMessage,
  Modal,
  Selectbox,
  TextArea,
} from "@oneleet/components";

const disableMonitorSchema = z.object({
  disabledReason: z.string().min(1),
  reviewRemindAt: z.string(),
});

type DisableMonitorSchema = z.infer<typeof disableMonitorSchema>;

export default function DisableMonitorModal({
  isOpen,
  monitor,
  onCancel,
  onSave,
}: {
  isOpen: boolean;
  monitor: Monitor;
  onCancel: () => void;
  onSave: ({
    disabledReason,
    reviewRemindAt,
  }: {
    disabledReason: string;
    reviewRemindAt: string;
  }) => Promise<void>;
}) {
  const { control, register, handleSubmit, formState, reset } =
    useForm<DisableMonitorSchema>({
      mode: "onChange",
      resolver: zodResolver(disableMonitorSchema),
      defaultValues: monitor.isEnabled
        ? {}
        : {
            disabledReason: monitor.disabledReason,
            reviewRemindAt: formatISO(
              new Date(monitor.reviewRemindAt ?? new Date()),
            ),
          },
    });

  function handleClose() {
    reset();
    onCancel();
  }

  const remindAtOptions = useMemo(() => {
    const now = new Date();
    const tomorrow = add(now, { days: 1 });
    const threeDays = add(now, { days: 3 });
    const nextWeek = add(now, { days: 7 });
    const twoWeeks = add(now, { days: 14 });
    const nextMonth = add(now, { months: 1 });
    const threeMonths = add(now, { months: 3 });
    const sixMonths = add(now, { months: 6 });
    const nextYear = add(now, { years: 1 });

    return [
      ...(monitor.reviewRemindAt
        ? [
            {
              id: formatISO(new Date(monitor.reviewRemindAt)),
              value: formatISO(new Date(monitor.reviewRemindAt)),
              label: `Current value — ${format(monitor.reviewRemindAt, "PP")}`,
            },
          ]
        : []),
      {
        id: formatISO(tomorrow),
        value: formatISO(tomorrow),
        label: "Tomorrow",
      },
      {
        id: formatISO(threeDays),
        value: formatISO(threeDays),
        label: "In 3 days",
      },
      {
        id: formatISO(nextWeek),
        value: formatISO(nextWeek),
        label: "Next week",
      },
      {
        id: formatISO(twoWeeks),
        value: formatISO(twoWeeks),
        label: "In 2 weeks",
      },
      {
        id: formatISO(nextMonth),
        value: formatISO(nextMonth),
        label: "Next month",
      },
      {
        id: formatISO(threeMonths),
        value: formatISO(threeMonths),
        label: "In 3 months",
      },
      {
        id: formatISO(sixMonths),
        value: formatISO(sixMonths),
        label: "In 6 months",
      },
      {
        id: formatISO(nextYear),
        value: formatISO(nextYear),
        label: "Next year",
      },
    ];
  }, [monitor]);

  return (
    <AnimatePresence>
      {isOpen && (
        <Modal onClose={handleClose}>
          <Modal.Title>{`Disable “${monitor.monitorType.name}” monitor`}</Modal.Title>
          <form onSubmit={handleSubmit(onSave)}>
            <div>
              <div className="mt-4 flex flex-col gap-y-6 text-left">
                <div>
                  <FormMessage
                    text={"Please provide a reason for disabling this monitor."}
                    className="mt-3"
                  />
                  <TextArea {...register("disabledReason")} className="mt-2" />
                  <Controller
                    control={control}
                    name="reviewRemindAt"
                    render={({ field }) => (
                      <>
                        <FormMessage
                          content="We recommend regularly reviewing disabled monitors to ensure they remain intentionally disabled. When would you next like to review this monitor?"
                          className="mb-3 mt-3"
                        />
                        <Selectbox
                          align="start"
                          options={remindAtOptions}
                          value={remindAtOptions.find(
                            (option) => option.value === field.value,
                          )}
                          onSelect={(option) => {
                            if (option) {
                              field.onChange(option.value);
                            }
                          }}
                          placeholder="Remind me to review this..."
                          enableClearingSelection={false}
                        />
                      </>
                    )}
                  />
                </div>
              </div>
            </div>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row-reverse">
              <Button
                type="submit"
                label="Save"
                variant="solid"
                color="brand"
                size="large"
                className="w-full sm:w-auto"
                isLoading={formState.isSubmitting}
                disabled={!formState.isValid}
              />
              <Button
                type="reset"
                label="Cancel"
                variant="outlined"
                color="gray"
                size="large"
                className="w-full sm:w-auto"
                onClick={handleClose}
              />
            </div>
          </form>
        </Modal>
      )}
    </AnimatePresence>
  );
}
